import api from './api';
import type { ApiResponse } from '@/types';
import type {
  Photographer,
  PhotographerEvent,
  PhotographerCustomer,
  PhotographerOrder,
  PhotographerAnalytics,
  PhotographerOnboardingForm,
  BrandingSettingsForm,
  OnboardingStatus,
  CreateEventPayload,
  PlanTier,
  BillingInterval,
  CurrentSubscription,
  CheckoutSessionResponse,
  PortalSessionResponse,
  PlanChangePreview,
} from '@/types/photographer';
import type { ShowcaseSelfStatus } from '@/types/showcase';
import type { WatermarkConfig } from '@/types/watermark';

// Photographer Profile API
export const photographerAPI = {
  // Profile Management
  getProfile: async (): Promise<ApiResponse<Photographer>> => {
    const response = await api.get('/photographer/profile');
    return response.data;
  },

  updateProfile: async (updates: Partial<Photographer>): Promise<ApiResponse<Photographer>> => {
    const response = await api.patch('/photographer/profile', updates);
    return response.data;
  },

  getOnboardingStatus: async (): Promise<ApiResponse<OnboardingStatus>> => {
    const response = await api.get('/photographer/onboarding');
    return response.data;
  },

  // Onboarding
  onboardPhotographer: async (
    data: PhotographerOnboardingForm
  ): Promise<ApiResponse<Photographer>> => {
    const response = await api.post('/photographer/onboard', data);
    return response.data;
  },

  // Domain Management
  checkSubdomainAvailability: async (
    subdomain: string
  ): Promise<ApiResponse<{ available: boolean }>> => {
    const response = await api.get(`/photographer/subdomain/check/${subdomain}`);
    return response.data;
  },

  updateDomainConfig: async (
    config: Partial<Photographer['domainConfig']>
  ): Promise<ApiResponse<Photographer>> => {
    const response = await api.patch('/photographer/domain', config);
    return response.data;
  },

  // Branding Management
  updateBrandingSettings: async (
    settings: Partial<BrandingSettingsForm>
  ): Promise<ApiResponse<Photographer>> => {
    const response = await api.patch('/photographer/branding', settings);
    return response.data;
  },

  uploadBrandingLogo: async (
    file: File
  ): Promise<ApiResponse<{ logoUrl: string; photographer: Photographer }>> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/photographer/branding/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updateWatermarkSettings: async (config: WatermarkConfig): Promise<ApiResponse<Photographer>> => {
    const response = await api.patch('/photographer/watermark', config);
    return response.data;
  },

  reapplyWatermarkSettings: async (): Promise<
    ApiResponse<{ eventsUpdated: number; queued: number; skipped: number }>
  > => {
    const response = await api.post('/photographer/watermark/reapply');
    return response.data;
  },

  // Events Management
  getEvents: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ApiResponse<PhotographerEvent[]>> => {
    const response = await api.get('/photographer/events', { params });
    return response.data;
  },

  getEvent: async (eventId: string): Promise<ApiResponse<PhotographerEvent>> => {
    const response = await api.get(`/photographer/events/${eventId}`);
    return response.data;
  },

  createEvent: async (
    eventData: CreateEventPayload
  ): Promise<ApiResponse<PhotographerEvent>> => {
    const response = await api.post('/photographer/events', eventData);
    return response.data;
  },

  updateEvent: async (
    eventId: string,
    updates: Partial<PhotographerEvent>
  ): Promise<ApiResponse<PhotographerEvent>> => {
    const response = await api.patch(`/photographer/events/${eventId}`, updates);
    return response.data;
  },

  reapplyEventWatermark: async (
    eventId: string
  ): Promise<ApiResponse<{ queued: number; skipped: number }>> => {
    const response = await api.post(`/photographer/events/${eventId}/watermark/reapply`);
    return response.data;
  },

  deleteEvent: async (eventId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete(`/photographer/events/${eventId}`);
    return response.data;
  },

  // Photo Management (presigned direct-to-S3 upload)
  getUploadUrl: async (
    eventId: string,
    filename: string,
    contentType: string
  ): Promise<ApiResponse<{
    uploadUrl: string;
    uploadFields: Record<string, string>;
    s3Key: string;
    mediaId: string;
    expiresAt: string;
  }>> => {
    const response = await api.post(
      `/photographer/events/${eventId}/photos/upload-url`,
      null,
      { params: { filename, content_type: contentType } }
    );
    return response.data;
  },

  uploadToS3: async (
    uploadUrl: string,
    uploadFields: Record<string, string>,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<void> => {
    const form = new FormData();
    Object.entries(uploadFields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);
    // Raw axios (not the `api` client) so our Authorization header is NOT sent to S3.
    const axios = (await import('axios')).default;
    await axios.post(uploadUrl, form, {
      onUploadProgress: e => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },

  listEventPhotos: async (
    eventId: string,
    params?: { page?: number; limit?: number }
  ): Promise<ApiResponse<Array<{
    id: string; eventId: string; filename: string;
    thumbnailUrl: string | null; webUrl: string | null;
    processingStatus: string; ready: boolean;
    width: number | null; height: number | null;
  }>>> => {
    const response = await api.get(`/photographer/events/${eventId}/photos`, { params });
    return response.data;
  },

  deleteEventPhoto: async (mediaId: string, eventId: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/photographer/photos/${mediaId}`, {
      params: { event_id: eventId },
    });
    return response.data;
  },

  // Showcase submission (album showcase, admin-approved)
  submitToShowcase: async (eventId: string): Promise<ApiResponse<ShowcaseSelfStatus>> => {
    const response = await api.post(`/photographer/events/${eventId}/showcase`);
    return response.data;
  },

  withdrawFromShowcase: async (eventId: string): Promise<ApiResponse<ShowcaseSelfStatus>> => {
    const response = await api.delete(`/photographer/events/${eventId}/showcase`);
    return response.data;
  },

  getShowcaseStatus: async (eventId: string): Promise<ApiResponse<ShowcaseSelfStatus>> => {
    const response = await api.get(`/photographer/events/${eventId}/showcase`);
    return response.data;
  },

  updatePhotoMetadata: async (photoId: string, metadata: any): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/photographer/photos/${photoId}`, metadata);
    return response.data;
  },

  // Customer Management
  getCustomers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<PhotographerCustomer[]>> => {
    const response = await api.get('/photographer/customers', { params });
    return response.data;
  },

  getCustomer: async (customerId: string): Promise<ApiResponse<PhotographerCustomer>> => {
    const response = await api.get(`/photographer/customers/${customerId}`);
    return response.data;
  },

  exportCustomers: async (format: 'csv' | 'xlsx', filters?: any): Promise<Blob> => {
    const response = await api.get('/photographer/customers/export', {
      params: { format, ...filters },
      responseType: 'blob',
    });
    return response.data;
  },

  // Order Management
  getOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    customerId?: string;
    eventId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ApiResponse<PhotographerOrder[]>> => {
    const response = await api.get('/photographer/orders', { params });
    return response.data;
  },

  getOrder: async (orderId: string): Promise<ApiResponse<PhotographerOrder>> => {
    const response = await api.get(`/photographer/orders/${orderId}`);
    return response.data;
  },

  updateOrderStatus: async (
    orderId: string,
    status: string,
    notes?: string
  ): Promise<ApiResponse<PhotographerOrder>> => {
    const response = await api.patch(`/photographer/orders/${orderId}`, { status, notes });
    return response.data;
  },

  processOrderFulfillment: async (orderId: string): Promise<ApiResponse<PhotographerOrder>> => {
    const response = await api.post(`/photographer/orders/${orderId}/fulfill`);
    return response.data;
  },

  // Analytics
  getAnalytics: async (params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PhotographerAnalytics>> => {
    const response = await api.get('/photographer/analytics', { params });
    return response.data;
  },

  getRevenueData: async (params?: {
    period?: 'day' | 'week' | 'month';
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<Array<{ date: string; amount: number; orders: number }>>> => {
    const response = await api.get('/photographer/analytics/revenue', { params });
    return response.data;
  },

  getTrafficData: async (params?: {
    period?: 'day' | 'week' | 'month';
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<Array<{ date: string; views: number; uniqueVisitors: number }>>> => {
    const response = await api.get('/photographer/analytics/traffic', { params });
    return response.data;
  },

  // Stripe Connect
  createConnectAccount: async (): Promise<ApiResponse<{ accountLinkUrl: string }>> => {
    const response = await api.post('/photographer/stripe/connect');
    return response.data;
  },

  getConnectAccountStatus: async (): Promise<
    ApiResponse<{
      accountId: string;
      chargesEnabled: boolean;
      payoutsEnabled: boolean;
      detailsSubmitted: boolean;
      requiresAction: boolean;
      actionUrl?: string;
    }>
  > => {
    const response = await api.get('/photographer/stripe/connect/status');
    return response.data;
  },

  // Settings
  updateNotificationSettings: async (settings: {
    newOrders: boolean;
    orderUpdates: boolean;
    paymentReceived: boolean;
    weeklyReports: boolean;
    monthlyReports: boolean;
  }): Promise<ApiResponse<void>> => {
    const response = await api.patch('/photographer/settings/notifications', settings);
    return response.data;
  },

  updateBusinessHours: async (hours: {
    timezone: string;
    hours: Array<{
      day: string;
      isOpen: boolean;
      openTime?: string;
      closeTime?: string;
    }>;
  }): Promise<ApiResponse<void>> => {
    const response = await api.patch('/photographer/settings/business-hours', hours);
    return response.data;
  },

  // Bulk Operations
  bulkUpdatePhotos: async (
    eventId: string,
    photoIds: string[],
    updates: any
  ): Promise<ApiResponse<void>> => {
    const response = await api.patch(`/photographer/events/${eventId}/photos/bulk`, {
      photoIds,
      updates,
    });
    return response.data;
  },

  bulkDeletePhotos: async (photoIds: string[]): Promise<ApiResponse<void>> => {
    const response = await api.delete('/photographer/photos/bulk', {
      data: { photoIds },
    });
    return response.data;
  },

  exportEventData: async (eventId: string, format: 'csv' | 'xlsx'): Promise<Blob> => {
    const response = await api.get(`/photographer/events/${eventId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Canonical subscription API (prefix /subscriptions). The router returns
// bare Pydantic models, NOT the {success,data} envelope, so each method reads
// response.data exactly once.
export interface CreateCheckoutBody {
  plan: PlanTier;
  interval: BillingInterval;
  success_url: string;
  cancel_url: string;
}

export const subscriptionAPI = {
  getCurrent: async (photographerId: string): Promise<CurrentSubscription> => {
    const response = await api.get(`/subscriptions/${photographerId}/current`);
    return response.data;
  },

  createCheckout: async (
    photographerId: string,
    body: CreateCheckoutBody
  ): Promise<CheckoutSessionResponse> => {
    const response = await api.post(`/subscriptions/${photographerId}/checkout`, body);
    return response.data;
  },

  createPortal: async (
    photographerId: string,
    returnUrl: string
  ): Promise<PortalSessionResponse> => {
    const response = await api.post(`/subscriptions/${photographerId}/portal`, {
      return_url: returnUrl,
    });
    return response.data;
  },

  previewChange: async (
    photographerId: string,
    newPlan: PlanTier,
    newInterval?: BillingInterval
  ): Promise<PlanChangePreview> => {
    const response = await api.post(`/subscriptions/${photographerId}/preview-change`, {
      new_plan: newPlan,
      ...(newInterval ? { new_interval: newInterval } : {}),
    });
    return response.data;
  },

  changePlan: async (
    photographerId: string,
    newPlan: PlanTier,
    newInterval?: BillingInterval
  ): Promise<{ success: boolean; status: string; new_plan: PlanTier }> => {
    const response = await api.post(`/subscriptions/${photographerId}/change-plan`, {
      new_plan: newPlan,
      ...(newInterval ? { new_interval: newInterval } : {}),
    });
    return response.data;
  },

  cancel: async (photographerId: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/subscriptions/${photographerId}/cancel`, {
      cancel_at_period_end: true,
    });
    return response.data;
  },

  reactivate: async (photographerId: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/subscriptions/${photographerId}/reactivate`);
    return response.data;
  },
};

import type { ConnectStatus } from '@/types/photographer';

export const connectAPI = {
  getStatus: async (): Promise<ConnectStatus> => {
    const response = await api.get('/photographer/connect/status');
    return response.data;
  },
  startOnboarding: async (): Promise<{ url: string; expires_at: number }> => {
    const response = await api.post('/photographer/connect/onboard', {});
    return response.data;
  },
  getDashboardLink: async (): Promise<{ url: string }> => {
    const response = await api.post('/photographer/connect/dashboard-link');
    return response.data;
  },
  publishEvent: async (eventId: string) => {
    const response = await api.post(`/photographer/events/${eventId}/publish`);
    return response.data;
  },
  unpublishEvent: async (eventId: string) => {
    const response = await api.post(`/photographer/events/${eventId}/unpublish`);
    return response.data;
  },
};
